import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CalendarClock, CircleCheck, House, QrCode, Stethoscope, UserRound } from "lucide-react";
import {
  formatUtcInstantForLocale,
  parseUtcInstant,
} from "@ascleo/types/date-time-validity";
import { QRCodeSVG } from "qrcode.react";
import {
  useSchedulerSend,
  useSchedulerSelector,
} from "../context";
import { schedulerSelectors } from "../selectors";
import { generateCalendarEvent } from "../../../../../lib/calendar-event";
import { useAudio } from "../../../../../lib/use-audio";
import { useTimedTerminal } from "../../../use-timed-terminal";
import { TimedTerminalFooter } from "../../../components/TimedTerminalFooter";
import { KioskStep, KioskStepColumns } from "../../../../../components/kiosk-step";
import { KioskStatePanel } from "../../../../../components/kiosk-state-panel";
import { useStepPrimaryAction } from "../../../context";
import { useAppointmentPrintController } from "../use-appointment-print-controller";
import { useSelfServiceTerminalSessionLease } from "../../../context";
import { AppointmentPrintAction } from "./AppointmentPrintAction";
import { useAppointmentTypeName } from "./appointment-type-name";

const AUTO_REDIRECT_SECONDS = 120;

interface SchedulerDoneStepProps {
  onComplete?: () => void;
}

/**
 * Scheduler success/done step: the confirmed booking as one content-sized
 * success panel with the key facts large (type, date, time, name; KO-14,
 * KO-67), the add-to-calendar QR and print beside it, one countdown with
 * "Mehr Zeit" (KO-18), and "Zurück zum Start" in the footer (KO-09). The raw
 * booking id is not shown; it stays in the DOM only as a hidden hook for the
 * live cleanup suite.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <SchedulerDoneStep onComplete={() => undefined} />
 * ```
 */
export function SchedulerDoneStep({ onComplete }: SchedulerDoneStepProps) {
  const { t, i18n } = useTranslation();
  const send = useSchedulerSend();
  const patient = useSchedulerSelector(schedulerSelectors.patient);
  const slotStart = useSchedulerSelector((s) => s.context.slotStart);
  const bookingId = useSchedulerSelector((s) => s.context.bookingId);
  const slotEnd = useSchedulerSelector((s) => s.context.slotEnd);
  const appointmentTypeId = useSchedulerSelector(schedulerSelectors.appointmentTypeId);
  const capturedTypeName = useSchedulerSelector(schedulerSelectors.appointmentTypeName);
  const appointmentTypeName = useAppointmentTypeName(appointmentTypeId, capturedTypeName);
  const locale = i18n.resolvedLanguage ?? i18n.language ?? "de";
  const print = useAppointmentPrintController({
    booking:
      bookingId !== undefined && patient?.id !== undefined && slotStart !== undefined && slotEnd !== undefined
        ? {
            scheduleId: bookingId,
            patientId: patient.id,
            patientDisplayName: `${patient.firstName} ${patient.lastName}`,
            startTime: slotStart,
            endTime: slotEnd,
          }
        : undefined,
    locale: i18n.resolvedLanguage ?? i18n.language ?? "de",
    labels: {
      title: t("self-service.scheduler.done.print-title"),
      patient: t("self-service.scheduler.confirmation.patient"),
      appointment: t("self-service.scheduler.confirmation.appointment"),
      location: t("self-service.scheduler.done.print-location"),
      mapHint: t("self-service.scheduler.done.print-map-hint"),
    },
  });
  useSelfServiceTerminalSessionLease(true);
  const expiryHandled = useRef(false);

  // Play audio on mount
  useAudio("scheduler-done");

  // Shared timed terminal with "more time" and reassurance overlay
  const { countdown, hasExtended, extendTime, restart } = useTimedTerminal({
    initialSeconds: AUTO_REDIRECT_SECONDS,
    paused: print.active,
    onExpire: () => {
      void print.revoke();
      send({ type: "RESET" });
      onComplete?.();
    },
  });

  useEffect(function restartDoneCountdownAfterPrintReturn() {
    if (
      print.state.kind === "cancelled" ||
      print.state.kind === "system-completed" ||
      print.state.kind === "unknown" ||
      print.state.kind === "failed"
    ) restart(AUTO_REDIRECT_SECONDS);
  }, [print.state.kind, restart]);

  useEffect(function clearExpiredPrintSession() {
    if (print.state.kind !== "expired" || expiryHandled.current) return;
    expiryHandled.current = true;
    void print.revoke();
    send({ type: "RESET" });
    onComplete?.();
  }, [onComplete, print, send]);

  const handleDone = async () => {
    await print.revoke();
    send({ type: "RESET" });
    onComplete?.();
  };

  useStepPrimaryAction({
    label: t("self-service.scheduler.done.back-to-start"),
    icon: House,
    tone: "success",
    testId: "scheduler-done-button",
    onClick: () => void handleDone(),
  });

  const slotDate = slotStart ? parseUtcInstant(slotStart) : null;
  // Generate calendar event QR code data
  const calendarEventData = useMemo(() => {
    if (!slotStart || !slotEnd || !patient) return null;

    return generateCalendarEvent({
      title: `Arzttermin - ${patient.firstName} ${patient.lastName}`,
      description: "Termin in der Praxis",
      startTime: slotStart,
      endTime: slotEnd,
      organizerName: "Praxis",
    });
  }, [slotStart, slotEnd, patient]);

  return (
    <KioskStep width="default" title={t("self-service.scheduler.done.title")}>
      <KioskStepColumns
        aside={
          <>
            {calendarEventData && (
              <section
                data-testid="scheduler-done-calendar-qr"
                className="flex min-w-0 flex-col items-center gap-3 rounded-3xl border bg-card p-6 text-center"
              >
                <h2 className="m-0 flex items-center gap-2 text-lg font-semibold">
                  <QrCode aria-hidden className="size-6" />
                  {t("self-service.scheduler.done.qr-hint")}
                </h2>
                <div className="rounded-2xl bg-white p-4">
                  <QRCodeSVG
                    value={calendarEventData}
                    size={180}
                    level="M"
                    fgColor="var(--foreground)"
                    bgColor="transparent"
                  />
                </div>
                <p className="m-0 max-w-[34ch] text-base text-muted-foreground">
                  {t("self-service.scheduler.done.qr-description")}
                </p>
              </section>
            )}
            {print.available ? (
              <AppointmentPrintAction
                active={print.active}
                state={print.state}
                onPrint={() => void print.print()}
                labels={{
                  action: t("self-service.scheduler.done.print-button"),
                  preparing: t("self-service.scheduler.done.print-preparing"),
                  error: t("self-service.scheduler.done.print-error-v2"),
                  returned: t("self-service.scheduler.done.print-returned"),
                }}
              />
            ) : null}
          </>
        }
      >
        <KioskStatePanel
          tone="success"
          testId="scheduler-done-panel"
          icon={<CircleCheck />}
          title={t("self-service.scheduler.done.title")}
          body={t("self-service.scheduler.done.subtitle")}
        >
          <dl className="m-0 grid w-full gap-3 text-start">
            {appointmentTypeName ? (
              <DoneFact icon={<Stethoscope />} label={t("self-service.scheduler.confirmation.type-label")} value={appointmentTypeName} />
            ) : null}
            {slotDate ? (
              <DoneFact
                icon={<CalendarClock />}
                label={t("self-service.scheduler.confirmation.appointment")}
                value={t("self-service.scheduler.confirmation.datetime-value", {
                  date: formatUtcInstantForLocale(slotDate, locale, {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  }),
                  time: formatUtcInstantForLocale(slotDate, locale, {
                    hour: "2-digit",
                    minute: "2-digit",
                  }),
                })}
              />
            ) : null}
            {patient ? (
              <DoneFact
                icon={<UserRound />}
                label={t("self-service.scheduler.confirmation.name-label")}
                value={`${patient.firstName} ${patient.lastName}`.trim()}
              />
            ) : null}
          </dl>
          {bookingId ? (
            <span hidden data-testid="scheduler-done-booking-id-text">
              {bookingId}
            </span>
          ) : null}
          <TimedTerminalFooter
            countdown={countdown}
            hasExtended={hasExtended}
            onExtendTime={extendTime}
          />
        </KioskStatePanel>
      </KioskStepColumns>
    </KioskStep>
  );
}

function DoneFact({
  icon,
  label,
  value,
}: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly value: string;
}) {
  return (
    <div className="flex min-w-0 items-start gap-3 rounded-2xl bg-background/70 p-3">
      <span aria-hidden className="grid size-11 shrink-0 place-items-center rounded-xl bg-success-subtle text-success-ink [&_svg]:size-6">
        {icon}
      </span>
      <div className="min-w-0">
        <dt className="text-base text-muted-foreground">{label}</dt>
        <dd className="m-0 text-xl font-semibold break-words text-foreground">{value}</dd>
      </div>
    </div>
  );
}
